import React, { createContext, useContext, useState, useCallback } from 'react';
import { CungVeDichAPI } from '../services/API';
import FeatureConfigCacheConfig from '../config/featureConfigCache';

/**
 * Feature Config Context
 * 
 * Quản lý feature configs cho user hiện tại
 * Gọi API checkMyFeatures để lấy danh sách features và isAccess
 * Cache kết quả trong memory và localStorage
 */

const FeatureConfigContext = createContext(null);

// Get cache config
const CACHE_CONFIG = FeatureConfigCacheConfig;
const CACHE_TIMEOUT = CACHE_CONFIG.timeout;
const CACHE_KEY = `${CACHE_CONFIG.keyPrefix}data`;
const CACHE_TIMESTAMP_KEY = `${CACHE_CONFIG.keyPrefix}timestamp`;

export const FeatureConfigProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);

  /**
   * Get storage instance based on config
   */
  const getStorage = useCallback(() => {
    if (!CACHE_CONFIG.enabled) return null;
    
    switch (CACHE_CONFIG.storageType) {
      case 'localStorage':
        return typeof window !== 'undefined' ? window.localStorage : null;
      case 'sessionStorage':
        return typeof window !== 'undefined' ? window.sessionStorage : null;
      case 'memory':
        return null; // Memory-only mode uses state only
      default:
        return typeof window !== 'undefined' ? window.localStorage : null;
    }
  }, []);

  /**
   * Load features from cache (localStorage/sessionStorage)
   */
  const loadFromCache = useCallback(() => {
    if (!CACHE_CONFIG.enabled) {
      if (CACHE_CONFIG.debug) console.log('⚠️ FeatureConfig: Cache disabled');
      return false;
    }

    try {
      const storage = getStorage();
      if (!storage) return false; // Memory-only mode or storage unavailable
      
      const cachedFeatures = storage.getItem(CACHE_KEY);
      const cachedTimestamp = storage.getItem(CACHE_TIMESTAMP_KEY);
      
      if (cachedFeatures && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();
        const age = now - timestamp;
        
        // Check if cache is still valid
        if (age < CACHE_TIMEOUT || CACHE_TIMEOUT === Infinity) {
          if (CACHE_CONFIG.debug) {
            console.log('✅ FeatureConfig: Loading from cache', {
              age: Math.round(age / 1000) + 's',
              storageType: CACHE_CONFIG.storageType
            });
          }
          
          const parsedFeatures = JSON.parse(cachedFeatures);
          setFeatures(parsedFeatures);
          setLastFetchTime(timestamp);
          return true;
        } else {
          if (CACHE_CONFIG.debug) console.log('⏰ FeatureConfig: Cache expired');
        }
      }
    } catch (error) {
      console.error('❌ FeatureConfig: Error loading from cache', error);
    }
    return false;
  }, [getStorage]);

  /**
   * Save features to cache (localStorage/sessionStorage)
   */
  const saveToCache = useCallback((featuresList) => {
    if (!CACHE_CONFIG.enabled) {
      if (CACHE_CONFIG.debug) console.log('⚠️ FeatureConfig: Cache disabled, skipping save');
      return;
    }

    try {
      const storage = getStorage();
      if (!storage) {
        // Memory-only mode - just update state
        if (CACHE_CONFIG.debug) console.log('💾 FeatureConfig: Memory-only mode');
        return;
      }
      
      const timestamp = Date.now();
      storage.setItem(CACHE_KEY, JSON.stringify(featuresList));
      storage.setItem(CACHE_TIMESTAMP_KEY, timestamp.toString());
      setLastFetchTime(timestamp);
      
      if (CACHE_CONFIG.debug) {
        console.log('💾 FeatureConfig: Saved to cache', {
          storageType: CACHE_CONFIG.storageType,
          count: featuresList.length
        });
      }
    } catch (error) {
      console.error('❌ FeatureConfig: Error saving to cache', error);
    }
  }, [getStorage]);

  /**
   * Load features from API
   */
  const loadFeatures = useCallback(async (accessToken, forceRefresh = false) => {
    if (!accessToken) {
      console.warn('⚠️ FeatureConfig: No access token provided');
      return { success: false, error: 'No access token' };
    }

    // Có cache → hiện ngay, đồng thời lấy bản mới từ DB chạy nền (admin khoá / sửa hướng dẫn là thấy ở lần mở trang kế tiếp,
    // không phải chờ hết 30 phút cache)
    if (!forceRefresh && loadFromCache()) {
      loadFeatures(accessToken, true);
      return { success: true, fromCache: true };
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log('🚀 FeatureConfig: Loading features from API');
      
      const result = await CungVeDichAPI.checkMyFeatures(accessToken);
      
      if (result.success && result.features) {
        console.log('✅ FeatureConfig: Loaded', result.features.length, 'features');
        
        setFeatures(result.features);
        saveToCache(result.features);
        
        return { 
          success: true, 
          features: result.features,
          fromCache: false 
        };
      } else {
        throw new Error('Invalid API response');
      }
    } catch (error) {
      console.error('❌ FeatureConfig: Error loading features', error);
      setError(error.message);
      
      // On error, try to use cache even if expired
      loadFromCache();
      
      return { 
        success: false, 
        error: error.message 
      };
    } finally {
      setIsLoading(false);
    }
  }, [loadFromCache, saveToCache]);

  /**
   * Refresh features from API
   */
  const refreshFeatures = useCallback(async (accessToken) => {
    console.log('🔄 FeatureConfig: Forcing refresh');
    return await loadFeatures(accessToken, true);
  }, [loadFeatures]);

  /**
   * Clear cache and features
   */
  const clearCache = useCallback(() => {
    try {
      const storage = getStorage();
      if (storage) {
        storage.removeItem(CACHE_KEY);
        storage.removeItem(CACHE_TIMESTAMP_KEY);
      }
      setFeatures([]);
      setLastFetchTime(null);
      
      if (CACHE_CONFIG.debug) {
        console.log('🗑️ FeatureConfig: Cache cleared', {
          storageType: CACHE_CONFIG.storageType
        });
      }
    } catch (error) {
      console.error('❌ FeatureConfig: Error clearing cache', error);
    }
  }, [getStorage]);

  /**
   * Check if user has access to a feature
   * @param {string} featureKey - Feature key to check (e.g. "PRODUCT_INVENTORY")
   * @returns {boolean} - True if user has access
   */
  const hasAccess = useCallback((featureKey) => {
    const feature = features.find(f => f.featureKey === featureKey);
    const access = feature?.isAccess || false;
    
    console.log(`🔐 FeatureConfig: hasAccess("${featureKey}") →`, access);
    return access;
  }, [features]);

  /**
   * Get feature by key
   * @param {string} featureKey - Feature key (e.g. "PRODUCT_INVENTORY")
   * @returns {object|null} - Feature object or null if not found
   */
  const getFeature = useCallback((featureKey) => {
    const feature = features.find(f => f.featureKey === featureKey);
    
    if (!feature) {
      console.warn(`⚠️ FeatureConfig: Feature "${featureKey}" not found`);
    }
    
    return feature || null;
  }, [features]);

  /**
   * Get feature by path
   * Maps path to featureKey and returns the feature
   * @param {string} path - Page path (e.g. "/kho-hang")
   * @returns {object|null} - Feature object or null if not found
   */
  const getFeatureByPath = useCallback((path) => {
    // Path to FeatureKey mapping - MUST match backend featureKey values
    const pathToKeyMap = {
      '/kho-hang': 'PRODUCT_INVENTORY',
      '/kho-hang/chinh-sua': 'PRODUCT_INVENTORY',
      '/ton-kho': 'PRODUCT_INVENTORY', // Backend uses /ton-kho path
      '/doanh-so': 'DOANH_SO', // ← Changed from SALE_REPORT to DOANH_SO
      '/doanh-so/chinh-sua': 'DOANH_SO',
      '/tra-hang': 'TRA_HANG', // ← Changed from SHIPMENT_REPORT to TRA_HANG
      '/tra-hang/chi-tiet': 'TRA_HANG',
      '/tra-hang/tao-moi': 'TRA_HANG',
      '/tra-hang/chinh-sua': 'TRA_HANG',
      '/don-hang-ban-buon': 'PRODUCT_INVENTORY',
      '/don-hang-ban-buon/tao-moi': 'PRODUCT_INVENTORY',
      '/don-hang': 'PRODUCT_INVENTORY',
      '/don-hang/tao-moi': 'PRODUCT_INVENTORY',
      // Backward compatibility
      '/shipment-reports': 'TRA_HANG',
      '/shipment-report-detail': 'TRA_HANG',
      '/shipment-report-form': 'TRA_HANG',
    };

    const featureKey = pathToKeyMap[path];
    
    if (!featureKey) {
      console.warn(`⚠️ FeatureConfig: No featureKey mapped for path "${path}"`);
      return null;
    }

    return getFeature(featureKey);
  }, [getFeature]);

  const value = {
    features,
    isLoading,
    error,
    lastFetchTime,
    loadFeatures,
    refreshFeatures,
    clearCache,
    hasAccess,
    getFeature,
    getFeatureByPath,
  };

  return (
    <FeatureConfigContext.Provider value={value}>
      {children}
    </FeatureConfigContext.Provider>
  );
};

/**
 * Hook to use feature config
 */
export const useFeatureConfig = () => {
  const context = useContext(FeatureConfigContext);
  
  if (!context) {
    throw new Error('useFeatureConfig must be used within FeatureConfigProvider');
  }
  
  return context;
};

export default FeatureConfigContext;
